/**
 * PricingPanel Component - Hedita Design System
 * Feature: Finance - Piano tariffario
 * Side Panel (Desktop) / Bottom Sheet (Mobile)
 */

import { useState } from "react";
import { Check, Zap, Building, Crown, ArrowRight, Sparkles } from "lucide-react";

const PIANI = [
  {
    id: "starter",
    nome: "Starter",
    prezzo: { mensile: 0, annuale: 0 },
    icon: Building,
    descrizione: "Per chi inizia con 1 struttura",
    strutture: 1,
    camere: 5,
    funzionalita: [
      { testo: "1 struttura", incluso: true },
      { testo: "Fino a 5 camere", incluso: true },
      { testo: "Calendario prenotazioni", incluso: true },
      { testo: "Sync iCal (1 feed)", incluso: true },
      { testo: "Check-in online", incluso: true },
      { testo: "Database ospiti (base)", incluso: true },
      { testo: "Guest App (base)", incluso: false },
      { testo: "Chatbot AI", incluso: false },
      { testo: "Report finanziari", incluso: false },
      { testo: "Team illimitato", incluso: false },
    ],
    cta: "Inizia gratis",
    attuale: false,
    popolare: false,
  },
  {
    id: "premium",
    nome: "Premium",
    prezzo: { mensile: 49, annuale: 39 },
    icon: Zap,
    descrizione: "Per host professionisti con più strutture",
    strutture: 5,
    camere: 30,
    funzionalita: [
      { testo: "Fino a 5 strutture", incluso: true },
      { testo: "Fino a 30 camere", incluso: true },
      { testo: "Calendario prenotazioni", incluso: true },
      { testo: "Sync iCal illimitata", incluso: true },
      { testo: "Check-in online", incluso: true },
      { testo: "Database ospiti completo", incluso: true },
      { testo: "Guest App completa", incluso: true },
      { testo: "Chatbot AI", incluso: true },
      { testo: "Report finanziari", incluso: true },
      { testo: "Team illimitato", incluso: false },
    ],
    cta: "Piano attuale",
    attuale: true,
    popolare: true,
  },
  {
    id: "business",
    nome: "Business",
    prezzo: { mensile: 99, annuale: 79 },
    icon: Crown,
    descrizione: "Per gestioni alberghiere complete",
    strutture: "Illimitate",
    camere: "Illimitate",
    funzionalita: [
      { testo: "Strutture illimitate", incluso: true },
      { testo: "Camere illimitate", incluso: true },
      { testo: "Tutte le funzionalità Premium", incluso: true },
      { testo: "API access", incluso: true },
      { testo: "White label", incluso: true },
      { testo: "Supporto prioritario", incluso: true },
      { testo: "Team illimitato", incluso: true },
      { testo: "Formazione dedicata", incluso: true },
      { testo: "SLA garantito", incluso: true },
      { testo: "Account manager", incluso: true },
    ],
    cta: "Contattaci",
    attuale: false,
    popolare: false,
  },
];

export function PricingPanel() {
  const [billing, setBilling] = useState<"mensile" | "annuale">("mensile");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-xl font-bold text-foreground flex items-center justify-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Piano Hedita
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Scegli il piano perfetto per il tuo business</p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex bg-secondary/30 p-1 rounded-xl">
          <button
            onClick={() => setBilling("mensile")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              billing === "mensile" 
                ? "bg-card text-foreground border border-border shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Mensile
          </button>
          <button
            onClick={() => setBilling("annuale")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              billing === "annuale" 
                ? "bg-card text-foreground border border-border shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Annuale
            <span className="ml-1.5 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px]">
              -20%
            </span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PIANI.map(piano => {
          const Icon = piano.icon;
          return (
            <div 
              key={piano.id}
              className={`relative bg-card border rounded-2xl p-5 flex flex-col ${
                piano.popolare 
                  ? "border-primary shadow-lg shadow-primary/10" 
                  : "border-border"
              }`}
            >
              {piano.popolare && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                  Più popolare
                </div>
              )}

              <div className="mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                  piano.popolare 
                    ? "bg-primary/10 text-primary" 
                    : "bg-secondary/50 text-muted-foreground"
                }`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-foreground text-lg">{piano.nome}</h3>
                <p className="text-xs text-muted-foreground mt-1">{piano.descrizione}</p>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-foreground">
                    €{piano.prezzo[billing]}
                  </span>
                  <span className="text-sm text-muted-foreground">/mese</span>
                </div>
                {billing === "annuale" && piano.prezzo.mensile > 0 && (
                  <p className="text-xs text-muted-foreground line-through">
                    €{piano.prezzo.mensile} mensile
                  </p>
                )}
              </div>

              <div className="flex-1 space-y-2 mb-4">
                {piano.funzionalita.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded flex items-center justify-center ${
                      f.incluso 
                        ? "bg-emerald-500/10 text-emerald-400" 
                        : "bg-muted/30 text-muted-foreground/30"
                    }`}>
                      <Check className="h-3 w-3" />
                    </div>
                    <span className={`text-xs ${f.incluso ? "text-foreground" : "text-muted-foreground/50"}`}>
                      {f.testo}
                    </span>
                  </div>
                ))}
              </div>

              <button 
                className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  piano.attuale 
                    ? "bg-muted text-muted-foreground cursor-default" 
                    : piano.popolare 
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-secondary text-foreground hover:bg-secondary/80 border border-border"
                }`}
                disabled={piano.attuale}
              >
                {piano.attuale ? "Piano attuale" : (
                  <>
                    {piano.cta} <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Enterprise Note */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Crown className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Hai bisogno di più?</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Contattaci per piani personalizzati con requisiti specifici o volumi enterprise.
            </p>
            <button className="mt-3 text-sm text-primary hover:underline">
              Contatta il team vendite →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
